/**
 * ApplicationForm Component
 *
 * Job application form with resume and cover letter upload.
 * Includes reCAPTCHA verification before submission.
 *
 * Requirements:
 * - JA-005: Application form component
 * - JA-006: Custom application form with file uploads
 *
 * @module components/careers/application-form
 */

import React, {useState, useRef} from 'react';
import PropTypes from 'prop-types';
import {defineMessages, FormattedMessage, useIntl} from 'react-intl';

import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Heading,
  HStack,
  Input,
  Text,
  useToast,
  VStack,
} from '@chakra-ui/react';

import {
  isValidApplicationPhone,
  sanitizePhone,
} from '@amblique/king-living/app/utils/validation';
import {useRecaptcha} from '@amblique/king-living/app/hooks/use-recaptcha';
import {
  RecaptchaDisclosure,
  RecaptchaErrorMessage,
  withRecaptcha,
} from '@amblique/king-living/app/components/recaptcha';
import {RECAPTCHA_ACTIONS} from '@amblique/king-living/app/constants/recaptcha';

const ACCEPTED_FILE_TYPES = '.doc,.docx,.pdf';
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const NOTICE_PERIOD_MAX_LENGTH = 50;

/** Live-site tokens (sample.html — .apply-careers / .job-application) */
const C = {
  bandBg: '#ebebeb',
  borderRule: '#c4c4c4',
  btnDark: '#333435',
  inputBg: '#ffffff',
  placeholder: '#1d1d1d',
  /** Softer invalid border (still 1px) — default Chakra red reads very strong. */
  inputInvalidBorder: '#fca5a5',
};

const applicationFormMessages = defineMessages({
  headingApply: {
    id: 'careers_application_form.heading.apply',
    defaultMessage: 'Apply Now',
  },
  introInstructions: {
    id: 'careers_application_form.intro.instructions',
    defaultMessage: 'To apply for this role please complete the form below.',
  },
  labelFirstName: {
    id: 'careers_application_form.label.first_name',
    defaultMessage: 'First Name',
  },
  labelLastName: {
    id: 'careers_application_form.label.last_name',
    defaultMessage: 'Last Name',
  },
  labelEmail: {
    id: 'careers_application_form.label.email',
    defaultMessage: 'Email',
  },
  labelPhone: {
    id: 'careers_application_form.label.phone',
    defaultMessage: 'Phone Number',
  },
  labelNoticePeriod: {
    id: 'careers_application_form.label.notice_period',
    defaultMessage: 'What is your notice period (if any)?',
  },
  placeholderFirstName: {
    id: 'careers_application_form.placeholder.first_name',
    defaultMessage: 'First Name*',
  },
  placeholderLastName: {
    id: 'careers_application_form.placeholder.last_name',
    defaultMessage: 'Last Name*',
  },
  placeholderEmail: {
    id: 'careers_application_form.placeholder.email',
    defaultMessage: 'Email*',
  },
  placeholderPhone: {
    id: 'careers_application_form.placeholder.phone',
    defaultMessage: 'Phone Number*',
  },
  placeholderNoticePeriod: {
    id: 'careers_application_form.placeholder.notice_period',
    defaultMessage: 'What is your notice period (if any)?',
  },
  errorNoticePeriodMaxLength: {
    id: 'careers_application_form.error.notice_period_max_length',
    defaultMessage: 'Notice period must be 50 characters or less',
  },
  toastValidationTitle: {
    id: 'careers_application_form.toast.validation_title',
    defaultMessage: 'Validation Error',
  },
  toastValidationDescription: {
    id: 'careers_application_form.toast.validation_description',
    defaultMessage: 'Please fill in all required fields correctly.',
  },
  toastSubmitSuccessTitle: {
    id: 'careers_application_form.toast.submit_success_title',
    defaultMessage: 'Application Submitted!',
  },
  toastSubmitSuccessDescription: {
    id: 'careers_application_form.toast.submit_success_description',
    defaultMessage: 'Thank you for your application. We will be in touch soon.',
  },
  toastSubmitFailedTitle: {
    id: 'careers_application_form.toast.submit_failed_title',
    defaultMessage: 'Submission Failed',
  },
  toastSubmitFailedDescription: {
    id: 'careers_application_form.toast.submit_failed_description',
    defaultMessage: 'Please try again later.',
  },
  errorSubmitGeneric: {
    id: 'careers_application_form.error.submit_generic',
    defaultMessage: 'Failed to submit application',
  },
  errorSubmitRetry: {
    id: 'careers_application_form.error.submit_retry',
    defaultMessage:
      'Failed to submit application. Please try again.',
  },
  // Matches the legacy King Living site copy ("This is a required field")
  errorRequiredField: {
    id: 'careers_application_form.error.required_field',
    defaultMessage: 'This is a required field',
  },
  errorEmailInvalid: {
    id: 'careers_application_form.error.email_invalid',
    defaultMessage: 'Please enter a valid email address',
  },
  errorPhoneInvalid: {
    id: 'careers_application_form.error.phone_invalid',
    defaultMessage: 'Please enter a valid number in this field',
  },
  errorFileType: {
    id: 'careers_application_form.error.file_type',
    defaultMessage:
      'Please upload a .doc, .docx, or .pdf file',
  },
  errorFileSize: {
    id: 'careers_application_form.error.file_size',
    defaultMessage: 'File size must be less than 5MB',
  },
  alertSuccessTitle: {
    id: 'careers_application_form.alert.success_title',
    defaultMessage: 'Application Submitted!',
  },
  alertSuccessDescription: {
    id: 'careers_application_form.alert.success_description',
    defaultMessage:
      'Thank you for applying for the {jobTitle} position. We will review your application and be in touch soon.',
  },
  sectionCoverLetter: {
    id: 'careers_application_form.section.cover_letter',
    defaultMessage: 'Cover Letter (Optional)',
  },
  sectionResume: {
    id: 'careers_application_form.section.resume',
    defaultMessage: 'Resume*',
  },
  fileAcceptedPrefix: {
    id: 'careers_application_form.file.accepted_prefix',
    defaultMessage: 'Accepted file types:',
  },
  fileAcceptedExtensions: {
    id: 'careers_application_form.file.accepted_extensions',
    defaultMessage: '.DOC .DOCX .PDF',
  },
  uploadButton: {
    id: 'careers_application_form.button.upload_file',
    defaultMessage: 'Upload file',
  },
  uploadNoFile: {
    id: 'careers_application_form.upload.no_file_chosen',
    defaultMessage: 'No file chosen',
  },
  submitButton: {
    id: 'careers_application_form.button.submit_application',
    defaultMessage: 'Submit Application',
  },
  submitButtonLoading: {
    id: 'careers_application_form.button.submit_loading',
    defaultMessage: 'Submitting...',
  },
  submitButtonAriaLabel: {
    id: 'careers_application_form.button.submit_aria_title',
    defaultMessage: 'Submit Application',
  },
});

const inputFieldStyles = {
  bg: C.inputBg,
  borderRadius: 0,
  borderWidth: '1px',
  borderColor: C.borderRule,
  fontSize: '12px',
  fontFamily: 'body',
  px: '12px',
  h: '36px',
  minH: '36px',
  lineHeight: '12px',
  py: '11px',
  _focusVisible: {borderColor: C.btnDark, boxShadow: 'none'},
  _invalid: {
    borderWidth: '1px',
    borderColor: C.inputInvalidBorder,
    boxShadow: 'none',
    _focusVisible: {
      borderColor: C.inputInvalidBorder,
      boxShadow: 'none',
    },
  },
  _placeholder: {color: C.placeholder, opacity: 1},
};

const formErrorMessageStyles = {
  color: 'red.600',
  fontSize: 'xs',
  mt: 1,
  mb: 0,
  lineHeight: 'short',
};

/** Arrow + tray — matches careers upload mock (stroke #1d1d1d via currentColor). */
const UploadFileIcon = (props) => (
  <Box
    as="span"
    display="inline-flex"
    alignItems="center"
    justifyContent="center"
    flexShrink={0}
    lineHeight={0}
    aria-hidden
    {...props}
  >
    <svg width="13" height="13" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M6 2.25V7.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
      <path
        d="M3.75 4.5L6 2.25 8.25 4.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M2.25 9.75h7.5"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  </Box>
);

/** Outlined upload CTA — unstyled variant avoids Chakra `size=md` px/h at md breakpoint. */
const uploadFileButtonStyles = {
  bg: 'transparent',
  color: C.placeholder,
  borderWidth: '1px',
  borderColor: 'black',
  borderStyle: 'solid',
  borderRadius: 0,
  fontFamily: 'body',
  fontWeight: 700,
  fontSize: '11px',
  lineHeight: 1,
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  h: '28px',
  minH: '28px',
  maxH: '28px',
  minW: 'unset',
  w: 'auto',
  py: 0,
  px: '19px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  iconSpacing: '8px',
  _hover: {bg: 'transparent', color: C.placeholder, borderColor: 'black'},
  _active: {bg: 'transparent', color: C.placeholder, borderColor: 'black'},
  _focusVisible: {
    outline: '2px solid',
    outlineColor: C.btnDark,
    outlineOffset: '2px',
  },
};

const uploadResultTextStyles = {
  fontStyle: 'italic',
  ml: {base: '10px', md: '12px'},
  fontSize: {base: 'xs', md: 'sm'},
  color: 'gray.500',
  fontWeight: 400,
  lineHeight: '14px',
};

const ApplicationForm = ({jobId, jobTitle, onSubmitSuccess}) => {
  const intl = useIntl();
  const toast = useToast();

  // reCAPTCHA v3 token verification (JA-006, GR-004/005/012).
  const {verify: verifyRecaptcha} = useRecaptcha();
  const resumeInputRef = useRef();
  const coverLetterInputRef = useRef();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Form data state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    noticePeriod: '',
  });

  // File state
  const [resumeFile, setResumeFile] = useState(null);
  const [coverLetterFile, setCoverLetterFile] = useState(null);

  // Validation errors
  const [errors, setErrors] = useState({});

  // Handle input changes
  const handleChange = (e) => {
    const {name, value} = e.target;
    const nextValue =
      name === 'phone'
        ? sanitizePhone(value)
        : name === 'noticePeriod'
          ? value.slice(0, NOTICE_PERIOD_MAX_LENGTH)
          : value;
    setFormData((prev) => ({...prev, [name]: nextValue}));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({...prev, [name]: null}));
    }
  };

  // Handle file selection
  const handleFileChange = (e, fileType) => {
    const file = e.target.files[0];

    if (!file) return;

    // Validate file type
    const validTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!validTypes.includes(file.type)) {
      setErrors((prev) => ({
        ...prev,
        [fileType]: intl.formatMessage(applicationFormMessages.errorFileType),
      }));
      return;
    }

    // Validate file size
    if (file.size > MAX_FILE_SIZE) {
      setErrors((prev) => ({
        ...prev,
        [fileType]: intl.formatMessage(applicationFormMessages.errorFileSize),
      }));
      return;
    }

    // Clear error and set file
    setErrors((prev) => ({...prev, [fileType]: null}));

    if (fileType === 'resume') {
      setResumeFile(file);
    } else {
      setCoverLetterFile(file);
    }
  };

  const scrollToFirstError = (fieldErrors) => {
    const fieldOrder = [
      'firstName',
      'lastName',
      'email',
      'phone',
      'noticePeriod',
      'coverLetter',
      'resume',
    ];
    const firstField = fieldOrder.find((field) => fieldErrors[field]);

    if (!firstField || typeof document === 'undefined') {
      return;
    }

    const targetId =
      firstField === 'resume'
        ? 'application-resume'
        : firstField === 'coverLetter'
          ? 'application-cover-letter'
          : firstField === 'noticePeriod'
            ? 'application-notice-period'
            : `application-${firstField === 'firstName' ? 'firstname' : firstField === 'lastName' ? 'lastname' : firstField}`;

    document.getElementById(targetId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    const requiredMsg = intl.formatMessage(
      applicationFormMessages.errorRequiredField
    );

    if (!formData.firstName.trim()) newErrors.firstName = requiredMsg;
    if (!formData.lastName.trim()) newErrors.lastName = requiredMsg;

    if (!formData.email.trim()) {
      newErrors.email = requiredMsg;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = intl.formatMessage(
        applicationFormMessages.errorEmailInvalid
      );
    }

    if (!formData.phone.trim()) {
      newErrors.phone = requiredMsg;
    } else if (!isValidApplicationPhone(formData.phone)) {
      newErrors.phone = intl.formatMessage(
        applicationFormMessages.errorPhoneInvalid
      );
    }

    if (!resumeFile) newErrors.resume = requiredMsg;

    if (formData.noticePeriod.trim().length > NOTICE_PERIOD_MAX_LENGTH) {
      newErrors.noticePeriod = intl.formatMessage(
        applicationFormMessages.errorNoticePeriodMaxLength
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0 ? null : newErrors;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    const fieldErrors = validateForm();
    if (fieldErrors) {
      scrollToFirstError(fieldErrors);
      toast({
        title: intl.formatMessage(applicationFormMessages.toastValidationTitle),
        description: intl.formatMessage(
          applicationFormMessages.toastValidationDescription
        ),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      // reCAPTCHA verification (JA-006, GR-012). The shared hook mints a token
      // and verifies it via /server/verify-recaptcha (scored server-side). On a
      // failed score, block the submission and surface the GR-012 message. When
      // the feature is off/unconfigured the hook returns success (fails open).
      const recaptcha = await verifyRecaptcha(RECAPTCHA_ACTIONS.JOB_APPLICATION);

      if (!recaptcha.success) {
        setSubmitError(recaptcha.message);
        setIsSubmitting(false);

        return;
      }

      // The token is verified client-side via the shared middleware (the same
      // path used by all in-scope forms), so the multipart submit carries the
      // sentinel that tells the jobs route its own verification is already done.
      const recaptchaToken = 'recaptcha_disabled';

      // Create FormData for multipart upload
      const submitData = new FormData();
      submitData.append('firstName', formData.firstName);
      submitData.append('lastName', formData.lastName);
      submitData.append('email', formData.email);
      submitData.append('phone', formData.phone);
      if (formData.noticePeriod.trim()) {
        submitData.append('noticePeriod', formData.noticePeriod.trim());
      }
      submitData.append('resume', resumeFile);
      submitData.append('recaptchaToken', recaptchaToken);

      if (coverLetterFile) {
        submitData.append('coverLetter', coverLetterFile);
      }

      // Submit application
      const response = await fetch(`/server/jobs/${jobId}/apply`, {
        method: 'POST',
        body: submitData,
      });

      const result = await response.json();

      if (!response.ok || !result.ok) {
        throw new Error(
          result.error ||
            intl.formatMessage(applicationFormMessages.errorSubmitGeneric)
        );
      }

      // Success
      setSubmitSuccess(true);
      toast({
        title: intl.formatMessage(
          applicationFormMessages.toastSubmitSuccessTitle
        ),
        description: intl.formatMessage(
          applicationFormMessages.toastSubmitSuccessDescription
        ),
        status: 'success',
        duration: 10000,
        isClosable: true,
      });

      onSubmitSuccess?.();
    } catch (error) {
      console.error('Application submission failed:', error);
      setSubmitError(
        error.message ||
          intl.formatMessage(applicationFormMessages.errorSubmitRetry)
      );

      toast({
        title: intl.formatMessage(
          applicationFormMessages.toastSubmitFailedTitle
        ),
        description:
          error.message ||
          intl.formatMessage(applicationFormMessages.toastSubmitFailedDescription),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Success state
  if (submitSuccess) {
    return (
      <Box bg="green.50" p={8} borderRadius="md" textAlign="center">
        <Alert
          status="success"
          variant="subtle"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          textAlign="center"
          height="200px"
          bg="transparent"
        >
          <AlertIcon boxSize="40px" mr={0} />
          <AlertTitle mt={4} mb={1} fontSize="lg">
            <FormattedMessage {...applicationFormMessages.alertSuccessTitle} />
          </AlertTitle>
          <AlertDescription maxWidth="sm">
            <FormattedMessage
              {...applicationFormMessages.alertSuccessDescription}
              values={{jobTitle}}
            />
          </AlertDescription>
        </Alert>
      </Box>
    );
  }

  return (
    <Box className="job-application" w="100%">
      <Heading
        as="h2"
        className="text-center"
        textAlign="center"
        fontSize={{base: '20px', md: '28px', lg: '42px'}}
        lineHeight="32px"
        fontWeight={400}
        fontFamily="body"
        fontStyle="normal"
        color="black"
        mb={{base: '7px', md: '24px', lg: '40px'}}
      >
        <FormattedMessage {...applicationFormMessages.headingApply} />
      </Heading>

      <Box className="std" textAlign="left" mb={{base: '15px', md: '23px'}}>
        <Text
          fontSize="14px"
          fontFamily="body"
          fontWeight={400}
          color="gray.800"
          lineHeight="1.5"
        >
          <FormattedMessage {...applicationFormMessages.introInstructions} />
        </Text>
      </Box>

      {/*
       * `noValidate` disables the browser's HTML5 required-field tooltip
       * ("Please fill out this field.") so our JS `validateForm()` runs and
       * renders the design-specified "This is a required field." copy under
       * each empty input. Without this, Chakra's `isRequired` propagates a
       * native `required` attribute to every input, the browser blocks submit
       * on the first empty one, and the rest of the inline errors never get
       * a chance to render.
       *
       * It also resolves the console warning
       *   "An invalid form control with name='' is not focusable. <input type='file'>"
       * which the browser logs because the hidden file input (display: none)
       * carries the `required` attribute via its `FormControl isRequired`
       * and is therefore not focusable for the native validation tooltip.
       */}
      <Box as="form" className="formbuilder" mt="-4px" noValidate onSubmit={handleSubmit}>
        <RecaptchaErrorMessage
          borderRadius={0}
          message={submitError}
          mb={4}
        />

        <VStack spacing={0} align="stretch">
          <FormControl isInvalid={!!errors.firstName} mb="13px">
            <FormLabel className="sr-only" htmlFor="application-firstname">
              <FormattedMessage {...applicationFormMessages.labelFirstName} />
            </FormLabel>
            <Input
              id="application-firstname"
              name="firstName"
              value={formData.firstName}
              onChange={handleChange}
              placeholder={intl.formatMessage(
                applicationFormMessages.placeholderFirstName
              )}
              aria-required
              {...inputFieldStyles}
            />
            <FormErrorMessage {...formErrorMessageStyles}>
              {errors.firstName}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.lastName} mb="13px">
            <FormLabel className="sr-only" htmlFor="application-lastname">
              <FormattedMessage {...applicationFormMessages.labelLastName} />
            </FormLabel>
            <Input
              id="application-lastname"
              name="lastName"
              value={formData.lastName}
              onChange={handleChange}
              placeholder={intl.formatMessage(
                applicationFormMessages.placeholderLastName
              )}
              aria-required
              {...inputFieldStyles}
            />
            <FormErrorMessage {...formErrorMessageStyles}>
              {errors.lastName}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.email} mb="13px">
            <FormLabel className="sr-only" htmlFor="application-email">
              <FormattedMessage {...applicationFormMessages.labelEmail} />
            </FormLabel>
            <Input
              id="application-email"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder={intl.formatMessage(
                applicationFormMessages.placeholderEmail
              )}
              aria-required
              {...inputFieldStyles}
            />
            <FormErrorMessage {...formErrorMessageStyles}>
              {errors.email}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.phone} mb="13px">
            <FormLabel className="sr-only" htmlFor="application-phone">
              <FormattedMessage {...applicationFormMessages.labelPhone} />
            </FormLabel>
            <Input
              id="application-phone"
              name="phone"
              type="tel"
              value={formData.phone}
              onChange={handleChange}
              placeholder={intl.formatMessage(
                applicationFormMessages.placeholderPhone
              )}
              aria-required
              {...inputFieldStyles}
            />
            <FormErrorMessage {...formErrorMessageStyles}>
              {errors.phone}
            </FormErrorMessage>
          </FormControl>

          <FormControl isInvalid={!!errors.noticePeriod} mb="13px">
            <FormLabel className="sr-only" htmlFor="application-notice-period">
              <FormattedMessage {...applicationFormMessages.labelNoticePeriod} />
            </FormLabel>
            <Input
              id="application-notice-period"
              name="noticePeriod"
              value={formData.noticePeriod}
              onChange={handleChange}
              placeholder={intl.formatMessage(
                applicationFormMessages.placeholderNoticePeriod
              )}
              maxLength={NOTICE_PERIOD_MAX_LENGTH}
              {...inputFieldStyles}
            />
            <FormErrorMessage {...formErrorMessageStyles}>
              {errors.noticePeriod}
            </FormErrorMessage>
          </FormControl>

          {/* Cover letter — fieldset 7: bottom rule */}
          <Box
            borderBottomWidth="1px"
            borderColor={C.borderRule}
            pt={{base: 0, md: '8px'}}
            pb={{base: '23px', md: '29px'}}
            mb="12px"
          >
            <Text
              display="block"
              fontSize={{base: '13px', md: '16px'}}
              lineHeight="20px"
              fontWeight={700}
              fontFamily="body"
              mb="2px"
              ml={0}
            >
              <FormattedMessage {...applicationFormMessages.sectionCoverLetter} />
            </Text>
            <FormControl isInvalid={!!errors.coverLetter}>
              <Text fontSize="sm" mb={2} className="no-margin">
                <FormattedMessage {...applicationFormMessages.fileAcceptedPrefix} />{' '}
                <Text as="strong" display="inline" fontWeight={700}>
                  <FormattedMessage
                    {...applicationFormMessages.fileAcceptedExtensions}
                  />
                </Text>
              </Text>
              <HStack align="center" flexWrap="wrap" spacing={0} className="upload-file">
                <Button
                  type="button"
                  variant="unstyled"
                  className="action upload"
                  onClick={() => coverLetterInputRef.current?.click()}
                  leftIcon={<UploadFileIcon />}
                  {...uploadFileButtonStyles}
                >
                  <FormattedMessage {...applicationFormMessages.uploadButton} />
                </Button>
                <Text
                  as="span"
                  className="upload-result"
                  noOfLines={1}
                  {...uploadResultTextStyles}
                >
                  {coverLetterFile ? (
                    coverLetterFile.name
                  ) : (
                    <FormattedMessage {...applicationFormMessages.uploadNoFile} />
                  )}
                </Text>
              </HStack>
              <Input
                id="application-cover-letter"
                ref={coverLetterInputRef}
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={(e) => handleFileChange(e, 'coverLetter')}
                display="none"
              />
              <FormErrorMessage {...formErrorMessageStyles}>
                {errors.coverLetter}
              </FormErrorMessage>
            </FormControl>
          </Box>

          {/* Resume — fieldset 8 */}
          <Box
            borderBottomWidth="1px"
            borderColor={C.borderRule}
            pt={{base: 0, md: '8px'}}
            pb={{base: '21px', md: '25px'}}
            mb="12px"
          >
            <Text
              display="block"
              fontSize={{base: '13px', md: '16px'}}
              lineHeight="20px"
              fontWeight={700}
              fontFamily="body"
              mb="2px"
              ml={0}
            >
              <FormattedMessage {...applicationFormMessages.sectionResume} />
            </Text>
            <FormControl isInvalid={!!errors.resume}>
              <Text fontSize="sm" mb={2} className="no-margin">
                <FormattedMessage {...applicationFormMessages.fileAcceptedPrefix} />{' '}
                <Text as="strong" display="inline" fontWeight={700}>
                  <FormattedMessage
                    {...applicationFormMessages.fileAcceptedExtensions}
                  />
                </Text>
              </Text>
              <HStack align="center" flexWrap="wrap" spacing={0} className="upload-file">
                <Button
                  type="button"
                  variant="unstyled"
                  className="action upload"
                  onClick={() => resumeInputRef.current?.click()}
                  leftIcon={<UploadFileIcon />}
                  {...uploadFileButtonStyles}
                >
                  <FormattedMessage {...applicationFormMessages.uploadButton} />
                </Button>
                <Text
                  as="span"
                  className="upload-result"
                  noOfLines={1}
                  {...uploadResultTextStyles}
                >
                  {resumeFile ? resumeFile.name : (
                    <FormattedMessage {...applicationFormMessages.uploadNoFile} />
                  )}
                </Text>
              </HStack>
              <Input
                id="application-resume"
                ref={resumeInputRef}
                name="resume"
                type="file"
                accept={ACCEPTED_FILE_TYPES}
                onChange={(e) => handleFileChange(e, 'resume')}
                display="none"
                aria-required="true"
              />
              <FormErrorMessage {...formErrorMessageStyles}>
                {errors.resume}
              </FormErrorMessage>
            </FormControl>
          </Box>

          {/* reCAPTCHA v3 runs invisibly at submit (no inline widget); keep the
              small spacer so the layout matches the live site. */}
          <Box
            className="field-recaptcha"
            minH={{base: '0', md: '8px'}}
            aria-hidden
          />

          <Box className="actions-toolbar submit-apply" mt={{base: '21px', md: '28px'}}>
            <Box className="primary" textAlign="left">
              <Button
                type="submit"
                id="apply-jobadder-button"
                title={intl.formatMessage(
                  applicationFormMessages.submitButtonAriaLabel
                )}
                className="action submit primary"
                width={{base: 'auto', md: '183px'}}
                minW={{base: '140px', md: '183px'}}
                borderRadius={0}
                borderWidth="1px"
                borderColor="transparent"
                bg={C.btnDark}
                color="white"
                fontFamily="body"
                fontWeight={700}
                fontSize={{base: '10px', md: '11px'}}
                lineHeight={{base: '14px', md: '1.2'}}
                py={{base: '4px', md: '6px'}}
                px={{base: '21px', md: '15px'}}
                textTransform="none"
                isLoading={isSubmitting}
                loadingText={intl.formatMessage(
                  applicationFormMessages.submitButtonLoading
                )}
                _hover={{bg: 'white', color: 'black', borderColor: 'black'}}
                _active={{bg: 'white', color: 'black', borderColor: 'black'}}
              >
                <FormattedMessage {...applicationFormMessages.submitButton} />
              </Button>
            </Box>
          </Box>

          {/* KL-1046 — required Google disclosure (reCAPTCHA badge is hidden globally). */}
          <RecaptchaDisclosure mt={{base: '12px', md: '16px'}} />
        </VStack>
      </Box>
    </Box>
  );
};

ApplicationForm.propTypes = {
  jobId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  jobTitle: PropTypes.string.isRequired,
  onSubmitSuccess: PropTypes.func,
};

// Wrap in the reCAPTCHA provider so `useRecaptcha` works on the job detail page.
export default withRecaptcha(ApplicationForm);
